import React, { memo, useCallback } from 'react'

import { type CellContext } from '@tanstack/react-table'
import { formatDistanceToNow } from 'date-fns'
import Link from 'next/link'
import {
  HiEllipsisHorizontal,
  HiOutlineEye,
  HiOutlineTag,
  HiOutlineTrash,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import Avatar from '@/components/common/Avatar'
import { Badge } from '@/components/common/BadgeV2'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import IconButton from '@/components/common/IconButtonV2'
import { ResourcePermissionGate } from '@/components/common/PermissionGate'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { DeleteCompaniesDialog } from '@/components/library-v2/companies/DeleteCompaniesDialog'
import { useThemeAdjustedCompanyColor } from '@/components/library-v2/companies/hooks'
import {
  cleanWebsiteUrl,
  getCompanyName,
} from '@/components/library-v2/companies/lib'
import {
  useCompaniesAddRelationshipStatusFilter,
  useCompaniesLastSelectedRowId,
  useCompaniesRelationshipStatusFilterIsActive,
  useCompaniesSetConfirmDialogOpen,
  useCompaniesSetLastSelectedRowId,
  useCompaniesSetSelectedCompanyIds,
} from '@/components/library-v2/companies/Table/context'
import {
  OurCompanyBadge,
  PendingBadge,
  badgeVariants,
} from '@/components/library-v2/ui/Badge'
import { NotSpecified } from '@/components/library-v2/ui/DetailPage'
import { RelationshipStatusBadge } from '@/components/library-v2/ui/RelationshipStatusBadge'
import { ShiftSelectCell } from '@/components/library-v2/ui/Table'
import { InlineTagSummary } from '@/components/tags/InlineTagSummary'
import { TagDropdownMenuEditSubContent } from '@/components/tags/TagDropdownMenuSubContent'

import { ensureDateStringIsUTC } from '@/helpers/date'
import { ensureAbsoluteUrl, getFaviconUrl } from '@/helpers/url'

import { useEntityTagCombobox } from '@/hooks/library/useUpdateEntityTags'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { EntityCompany } from '@/types/library/Entity'

import { usePermissionLoader } from '@/services/permission-loader/context'

interface CompanyAvatarProps {
  company: EntityCompany
  width?: number
  height?: number
}

export const CompanyAvatar: React.FC<CompanyAvatarProps> = memo(
  ({ company, width = 32, height = 32 }) => {
    const color = useThemeAdjustedCompanyColor({ company })
    const faviconUrl = company.website_url
      ? getFaviconUrl(company.website_url, 128)
      : undefined
    const fallback = faviconUrl === undefined
    return (
      <Avatar
        className={fallback ? undefined : 'rounded-none'}
        src={faviconUrl}
        name={getCompanyName(company)}
        width={width}
        height={height}
        noTooltip={true}
        style={{ backgroundColor: color }}
        initialShowFallback={fallback}
        startWithFallback={fallback}
      />
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.company.id === nextProps.company.id &&
      prevProps.width === nextProps.width &&
      prevProps.height === nextProps.height &&
      prevProps.company.website_url === nextProps.company.website_url
    )
  },
)

CompanyAvatar.displayName = 'LibraryV2.Companies.Table.CompanyAvatar'

export const CompanyNameCell: React.FC<CellContext<EntityCompany, unknown>> = ({
  row,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const company = row.original
  const name = getCompanyName(company)
  const tagIds = company.tag_ids ?? []
  return (
    <div className="flex items-center gap-3 py-3 pr-3 overflow-hidden">
      <div className="flex-shrink-0">
        <CompanyAvatar company={company} />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5 overflow-hidden">
        <Link
          className="block font-medium text-primary-900 dark:text-primary-100 hover:text-blue-600 dark:hover:text-blue-400 text-sm text-left cursor-pointer truncate"
          href={`/orgs/${activeOrgId}/library/companies/${company.id}`}
        >
          {name}
        </Link>
        {company.legal_name && company.legal_name !== company.name && (
          <div className="text-sm text-primary-500 dark:text-primary-400 truncate">
            {company.legal_name}
          </div>
        )}
        {tagIds.length > 0 ? <InlineTagSummary tagIds={tagIds} /> : null}
      </div>
    </div>
  )
}

export const CompanyBadgesCell: React.FC<
  CellContext<EntityCompany, unknown>
> = ({ row }) => {
  const { activeOrgId } = useActiveOrgId()
  const company = row.original
  const isOurCompany = company.linked_organization_id === activeOrgId

  const setConfirmDialogOpen = useCompaniesSetConfirmDialogOpen()
  const setSelectedIds = useCompaniesSetSelectedCompanyIds()
  const addRelationshipStatusFilter = useCompaniesAddRelationshipStatusFilter()
  const isStatusFilterActive = useCompaniesRelationshipStatusFilterIsActive(
    company.relationship_status,
  )

  const handleOpenConfirmDialogForCompany = useCallback(() => {
    setConfirmDialogOpen(true)
    setSelectedIds([company.id])
  }, [company.id, setConfirmDialogOpen, setSelectedIds])

  const handleFilterByRelationshipStatus = useCallback(() => {
    if (company.relationship_status && !isStatusFilterActive) {
      addRelationshipStatusFilter(company.relationship_status)
    }
  }, [
    company.relationship_status,
    isStatusFilterActive,
    addRelationshipStatusFilter,
  ])

  return (
    <div className="flex items-center justify-end gap-2 py-3 pr-3">
      {isOurCompany ? (
        <OurCompanyBadge />
      ) : (
        <RelationshipStatusBadge
          status={company.relationship_status}
          disabled={true}
          size="sm"
          onClick={handleFilterByRelationshipStatus}
        />
      )}
      {company.confirmed_by_user_id === null && (
        <ResourcePermissionGate
          resourceType="entity"
          resourceId={company.id}
          permission="write"
          fallback="hide"
        >
          <PendingBadge
            inTable={true}
            onClick={handleOpenConfirmDialogForCompany}
          />
        </ResourcePermissionGate>
      )}
    </div>
  )
}

export const IndustryCell: React.FC<CellContext<EntityCompany, unknown>> = ({
  row,
}) => {
  const industry = row.original.industry
  if (!industry) {
    return (
      <div className="py-3 pr-3">
        <span className="text-sm text-primary-500 dark:text-primary-400">
          Not specified
        </span>
      </div>
    )
  }
  return (
    <div className="inline-flex items-center py-3 pr-3">
      <Tooltip>
        <TooltipTrigger className="text-start cursor-default">
          <Badge
            variant="outline"
            className={twMerge(
              badgeVariants({ variant: 'blue' }),
              'line-clamp-1',
            )}
          >
            {industry}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <span>{industry}</span>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export const RelationshipCell: React.FC<
  CellContext<EntityCompany, unknown>
> = ({ row }) => {
  const relationshipTypes = row.original.relationship_types ?? []
  if (relationshipTypes.length === 0) {
    return (
      <div className="py-3 pr-3">
        <NotSpecified />
      </div>
    )
  }
  return (
    <div className="inline-flex items-center py-3 pr-3">
      <Tooltip>
        <TooltipTrigger className="text-start cursor-default">
          <div className="flex flex-wrap gap-1">
            {relationshipTypes.map((relationshipType) => (
              <Badge
                key={relationshipType}
                variant="outline"
                className="max-w-40 truncate text-xs"
              >
                {relationshipType}
              </Badge>
            ))}
          </div>
        </TooltipTrigger>
        <TooltipContent inverseColors={true}>
          <div className="flex max-w-80 flex-wrap gap-1">
            {relationshipTypes.map((relationshipType) => (
              <Badge key={relationshipType} variant="outline">
                {relationshipType}
              </Badge>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export const WebsiteUrlCell: React.FC<CellContext<EntityCompany, unknown>> = ({
  row,
}) => {
  const websiteUrl = row.original.website_url
  if (!websiteUrl) {
    return (
      <div className="py-3 pr-3">
        <NotSpecified />
      </div>
    )
  }
  return (
    <Link
      href={ensureAbsoluteUrl(websiteUrl)}
      target="_blank"
      rel="noreferrer"
      className="truncate line-clamp-1 text-sm text-blue-600 dark:text-blue-400 hover:underline underline-offset-2 pr-3"
    >
      <span className="truncate">{cleanWebsiteUrl(websiteUrl)}</span>
    </Link>
  )
}

export const LastContactedCell: React.FC<
  CellContext<EntityCompany, unknown>
> = ({ row }) => {
  const dt = row.original.last_contacted_at
  return (
    <span className="py-3 pr-3 text-sm text-primary-900 dark:text-primary-100">
      {dt ? (
        formatDistanceToNow(new Date(ensureDateStringIsUTC(dt)), {
          addSuffix: true,
        })
      ) : (
        <span className="text-primary-500 dark:text-primary-400">Never</span>
      )}
    </span>
  )
}

export const ActionsCell: React.FC<CellContext<EntityCompany, unknown>> = ({
  row,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const permissionLoader = usePermissionLoader()
  const company = row.original
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false)
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false)
  const skipNextTagResetRef = React.useRef<boolean>(false)
  const {
    comboboxOpen,
    selectedTagIds,
    hasPendingTagChanges,
    isClearSelected,
    isSaving,
    handleOpenChange,
    handleToggleTag,
    handleCancel,
    handleClear,
    handleSave,
  } = useEntityTagCombobox({ entity: company })

  const handleOpenDeleteDialog = React.useCallback(() => {
    setDeleteDialogOpen(true)
  }, [])

  // Pre-warm permission cache on hover to avoid flicker
  const handleMouseEnter = React.useCallback(() => {
    if (permissionLoader && company.id) {
      permissionLoader.loadMany([
        {
          resourceType: 'entity',
          resourceId: company.id,
          permission: 'write',
        },
      ])
    }
  }, [permissionLoader, company.id])

  const handleMenuOpenChange = React.useCallback(
    (open: boolean) => {
      setMenuOpen(open)

      if (open && permissionLoader && company.id) {
        void permissionLoader.loadMany([
          {
            resourceType: 'entity',
            resourceId: company.id,
            permission: 'write',
          },
        ])
      }

      if (!open && skipNextTagResetRef.current) {
        skipNextTagResetRef.current = false
        return
      }

      if (!open) {
        handleCancel()
      }
    },
    [company.id, handleCancel, permissionLoader],
  )

  const handleCancelTagEdit = React.useCallback(() => {
    handleCancel()
    setMenuOpen(false)
  }, [handleCancel])

  const handleSaveTagEdit = React.useCallback(async () => {
    const saved = await handleSave()

    if (!saved) {
      return
    }

    skipNextTagResetRef.current = true
    setMenuOpen(false)
  }, [handleSave])

  return (
    <div
      className={twMerge('flex items-center justify-end gap-1 pr-3')}
      onMouseEnter={handleMouseEnter}
    >
      {deleteDialogOpen && (
        <DeleteCompaniesDialog
          companyIds={[company.id]}
          open={deleteDialogOpen}
          setOpen={setDeleteDialogOpen}
        />
      )}
      <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
        <DropdownMenuTrigger asChild={true}>
          <IconButton
            tooltip="More Actions"
            tooltipProps={{ delayDuration: 500 }}
          >
            <HiEllipsisHorizontal
              size={16}
              className="flex-shrink-0 text-primary-900 dark:text-primary-100"
            />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <Link
              href={`/orgs/${activeOrgId}/library/companies/${company.id}`}
              className="flex items-center"
            >
              <HiOutlineEye
                size={16}
                className="flex-shrink-0 text-primary-700 dark:text-primary-300 mr-2"
              />
              <span>View Company</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSub open={comboboxOpen} onOpenChange={handleOpenChange}>
            <ResourcePermissionGate
              resourceType="entity"
              resourceId={company.id}
              permission="write"
              fallback="disable"
              asChild
            >
              <DropdownMenuSubTrigger className="flex items-center gap-x-2">
                <HiOutlineTag
                  size={16}
                  className="text-primary-500 dark:text-primary-400"
                />
                <span>Edit Tags</span>
              </DropdownMenuSubTrigger>
            </ResourcePermissionGate>
            <DropdownMenuSubContent className="overflow-hidden p-0">
              <TagDropdownMenuEditSubContent
                selectedTagIds={selectedTagIds}
                onSelect={handleToggleTag}
                onCancel={handleCancelTagEdit}
                onClear={handleClear}
                onSave={handleSaveTagEdit}
                clearActive={isClearSelected}
                isSaving={isSaving}
                saveDisabled={!hasPendingTagChanges}
              />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <ResourcePermissionGate
            resourceType="entity"
            resourceId={company.id}
            permission="write"
            fallback="disable"
            asChild
          >
            <DropdownMenuItem
              onClick={handleOpenDeleteDialog}
              className="flex items-center gap-x-2 text-red-600 dark:text-red-500"
            >
              <HiOutlineTrash className="w-4 h-4" />
              <span>Delete</span>
            </DropdownMenuItem>
          </ResourcePermissionGate>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export const CompaniesSelectCell: React.FC<
  CellContext<EntityCompany, unknown>
> = ({ row, table, cell }) => {
  const lastSelectedRowId = useCompaniesLastSelectedRowId()
  const setLastSelectedRowId = useCompaniesSetLastSelectedRowId()

  return (
    <ShiftSelectCell
      row={row}
      table={table}
      cell={cell}
      lastSelectedRowId={lastSelectedRowId}
      setLastSelectedRowId={setLastSelectedRowId}
    />
  )
}
